import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import {
  Activity, ChevronRight, Filter, Search, Plus
} from 'lucide-react';
import Pagination from '../components/Pagination';

/**
 * HealthLog — Symptom tracking page.
 * Patient: sees own symptoms. Doctor: sees all patient symptoms.
 */
const HealthLog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDoctor = user?.role === 'DOCTOR';
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);

  const fetchSymptoms = async () => {
    try {
      setLoading(true);
      const endpoint = isDoctor
        ? `/symptoms?status=${statusFilter}&page=${page}&limit=20`
        : `/symptoms/me?status=${statusFilter}&page=${page}&limit=20`;
      const [res, statsRes] = await Promise.all([
        api.get(endpoint),
        page === 1 ? api.get('/symptoms/stats/dashboard').catch(() => null) : Promise.resolve(null),
      ]);
      const data = res?.data?.data || res?.data || [];
      setSymptoms(Array.isArray(data) ? data : []);
      setTotalPages(res?.data?.pagination?.totalPages || res?.data?.totalPages || 1);
      if (statsRes?.data?.success) setStats(statsRes.data.data);
    } catch (err) {
      console.error('Failed to fetch symptoms:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSymptoms(); }, [statusFilter, page]);

  const getSevStyle = (sev) => {
    const m = {
      urgent: { bg: 'rgba(186,26,26,0.1)', color: '#ba1a1a', label: 'Urgent' },
      high: { bg: 'rgba(249,115,22,0.1)', color: '#F97316', label: 'High' },
      medium: { bg: 'rgba(95,67,0,0.1)', color: '#5f4300', label: 'Medium' },
      low: { bg: 'rgba(0,105,107,0.1)', color: '#00696b', label: 'Low' },
    };
    return m[sev] || m.low;
  };

  const getStatusStyle = (status) => {
    const m = {
      OPEN: 'bg-amber-100 text-amber-700',
      CLOSED: 'bg-slate-100 text-slate-600',
    };
    return m[status] || 'bg-slate-100 text-slate-600';
  };

  const severityCounts = symptoms.reduce((acc, s) => {
    const sev = s.severity?.toLowerCase() || 'low';
    acc[sev] = (acc[sev] || 0) + 1;
    return acc;
  }, {});

  const filtered = symptoms.filter(s => {
    const matchesSearch = !searchTerm || s.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'ALL' || s.severity?.toLowerCase() === severityFilter.toLowerCase();
    return matchesSearch && matchesSeverity;
  });

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">
            {isDoctor ? 'Patient Log' : 'My Health Log'}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isDoctor ? 'All symptom reports, consultations, and care events' : 'Track your symptoms over time'}
          </p>
        </div>
        {!isDoctor && (
          <button
            onClick={() => navigate('/report')}
            className="flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl font-bold text-sm shadow-md hover:shadow-lg active:scale-95 transition-all"
          >
            <Plus className="w-4 h-4" />
            Report Issue
          </button>
        )}
      </div>

      {/* Stats (doctor view) */}
      {isDoctor && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Reports', value: stats.totalSymptoms || 0 },
            { label: 'Active', value: stats.activeSymptoms || 0 },
            { label: 'Resolved', value: stats.resolvedCases || 0 },
            { label: 'Urgent', value: stats.urgentCases || 0 },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-outline-variant/10 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
              <p className="text-2xl font-extrabold tracking-tighter mb-0.5">{s.value}</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder={isDoctor ? 'Search by patient or symptom...' : 'Search symptoms...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            {['OPEN', 'CLOSED', 'ALL'].map((status) => (
              <button
                key={status}
                onClick={() => { setStatusFilter(status); setPage(1); }}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  statusFilter === status
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Severity</span>
          {[
            { value: 'ALL', label: 'All', count: symptoms.length },
            { value: 'urgent', label: 'Urgent', bg: 'bg-red-100 text-red-700', active: 'bg-red-600 text-white', count: severityCounts.urgent || 0 },
            { value: 'high', label: 'High', bg: 'bg-orange-100 text-orange-700', active: 'bg-orange-500 text-white', count: severityCounts.high || 0 },
            { value: 'medium', label: 'Medium', bg: 'bg-amber-100 text-amber-700', active: 'bg-amber-500 text-white', count: severityCounts.medium || 0 },
            { value: 'low', label: 'Low', bg: 'bg-teal-100 text-teal-700', active: 'bg-teal-600 text-white', count: severityCounts.low || 0 },
          ].map(({ value, label, bg, active, count }) => (
            <button
              key={value}
              onClick={() => { setSeverityFilter(value); setPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                severityFilter === value
                  ? value === 'ALL' ? 'bg-primary text-white border-primary shadow-sm' : `${active} border-transparent shadow-sm`
                  : value === 'ALL' ? 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50' : `${bg} border-transparent hover:opacity-80`
              }`}
            >
              {label}
              <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${
                severityFilter === value ? 'bg-white/25' : 'bg-black/10'
              }`}>{count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Symptom List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 rounded-2xl animate-pulse bg-slate-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-bold text-lg text-slate-900 mb-1">No symptoms found</h3>
          <p className="text-sm text-slate-500">
            {searchTerm ? 'Try a different search term.' : `No ${statusFilter.toLowerCase().replace('_', ' ')} symptoms.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((symptom) => {
            const sev = getSevStyle(symptom.severity);
            return (
              <button
                key={symptom._id}
                onClick={() => navigate(`/symptom/${symptom._id}`)}
                className="w-full bg-white rounded-2xl p-5 border border-outline-variant/10 hover:shadow-md transition-all flex items-center gap-4 text-left"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: sev.bg }}>
                  <Activity className="w-5 h-5" style={{ color: sev.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-bold text-sm text-slate-900 break-words">{symptom.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusStyle(symptom.status)}`}>
                      {symptom.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {isDoctor && symptom.userId?.name && (
                      <span className="font-medium text-slate-600">{symptom.userId.name}</span>
                    )}
                    <span>{new Date(symptom.createdAt).toLocaleDateString()}</span>
                    <span className="font-bold" style={{ color: sev.color }}>{sev.label}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default HealthLog;
